import { h, Component } from 'preact';
import { clearAllBodyScrollLocks } from 'body-scroll-lock';
import pure from 'utils/pure';
import Store from 'store/store';
import * as tracking from 'store/bolt/trackingV2';
import 'store/bolt';
import 'store/bolt/calendarRemind';
import 'store/bolt/cookies';
import 'store/bolt/ageGate';
import 'store/bolt/pixels';
import 'store/bolt/externalCookieBanner';
import 'store/bolt/subscriberPopup';
import 'store/bolt/trailerOverlay';
import 'utils/bolt/markdownSetUp';
import { accessNested, joinClasses } from 'utils/utils';
import tryToPlayVideo from 'utils/tryToPlayVideo';
import tabbingDetection from 'utils/tabbingDetection';
import hoverDetection from 'utils/hoverDetection';
import parseQueryString from 'utils/parseQueryString';
import { getValueForClientSize, addCSSRuleset, getPageIdFromURL, shouldShowShowtimes, navigateInternally, getHeaderHeight } from 'utils/bolt/index';
import { useDynamicAssetFolder } from 'utils/assets';
import { getPercentageThreshold } from 'utils/visibilityChecks';
import { getIsDesktopSize, getIsSinglePageIframed, isIframed } from 'utils/bolt/client';

import { getCookie } from 'utils/cookies';
import * as webp from 'utils/bolt/webp';
import { isWebPSupported } from 'utils/isWebPSupported';
import * as categories from 'utils/gdpr/categories';

import Header from 'components/bolt/header/header';
import Footer from 'components/bolt/footer/footer';
import LandscapeOverlay from 'components/bolt/landscapeOverlay/landscapeOverlay';
import DynamicPageLoader from 'components/shared/dynamicPageLoader/dynamicPageLoader';

import {
	pages,
	SidePanelLoader,
	VideoOverlayLoader
} from 'pages/bolt_pages';
import { pixelsCategories } from 'pages/bolt_features';

import s from 'base/bolt/boltApp.sss';

const videosWithError = {};
const isServer = typeof window === 'undefined';
if (!isServer) {
	window.navigateInternally = navigateInternally;
	window.powCategories = categories;
}

export default @pure class BoltApp extends Component {
	constructor(props) {
		super(props);

		const qs = parseQueryString();

		this.state.appState = props.initialStore;
		this.state.orientation = props.initialStore?.client?.orientation || 'portrait';
		this.state.bgImageLoaded = false;
		this.state.backgroundStyle = {};
		this.state.fallbackBackgroundStyle = {};
		this.state.posterVisible = true;
		this.state.bgVideoHidden = false;
		this.state.videoPlaySuccess = false;
		this.state.audioMuted = true;
		// Make isComponentLoaded "not loaded" only if the initial page is actually a page
		this.state.isComponentLoaded = !pages[this.state.appState.activePage];
		Store.set(props.initialStore);

		// Store.on('afterAll', (...data) => console.info('STORE EVENT:', ...data));
		Store.on('update', () => {
			const store = Store.get();
			this.setState({ appState: store });
		});

		if (!isServer) {
			this.runOnLoad();
		}

		this.scrolling = this.scrolling.bind(this);
		this.handleResize = this.handleResize.bind(this);
		this.handlePopState = this.handlePopState.bind(this);
		this.handleOrientationChange = this.handleOrientationChange.bind(this);
		this.updateVisibilityPercentages = this.updateVisibilityPercentages.bind(this);
		this.updateBgStyle = this.updateBgStyle.bind(this);
		this.hidePoster = this.hidePoster.bind(this);
		this.removeBgVideo = this.removeBgVideo.bind(this);
		this.playVideo = this.playVideo.bind(this);
		this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
		this.onPageContentLoad = this.onPageContentLoad.bind(this);
		this.toggleAudio = this.toggleAudio.bind(this);
		this.occluderRemoved = this.occluderRemoved.bind(this);

		let appData = props.initialStore?.appData;
		let features = appData?.options;

		this.hideHeader = qs['no-header'];
		this.getBgData();
		this.ageGateEnabled = features?.ageGate;
		this.externalLinkModalEnabled = features?.externalLinkModal === true || features?.externalLinkModal?.enabled;
		this.userFeedbackModalEnabled = !!features?.userFeedbackModal;

		this.powsterCookiesEnabled = features?.cookiesModal?.enableModal;
		this.tealiumCookiesEnabled = features?.enableTealiumCookieConsent;
		this.evidonCookiesEnabled = features?.requireUserConsent;
		this.ensightenCookiesEnabled = features?.enableEnsighten;
		this.oneTrustCookiesEnabled = features?.oneTrustCookieConsent;
		this.didomiCookiesEnabled = features?.didomiCookieConsent;
		this.trustArcCookiesEnabled = features?.trustArcCookieConsent;
		this.SMECookiesEnabled = features?.SMECookieConsent;
		this.useKetchCookies = features?.ketchCookieConsent;
		this.cookiesEnabled = this.powsterCookiesEnabled || this.tealiumCookiesEnabled || this.ensightenCookiesEnabled || this.evidonCookiesEnabled || this.oneTrustCookiesEnabled || this.didomiCookiesEnabled || this.SMECookiesEnabled || this.useKetchCookies || this.trustArcCookiesEnabled;
	}

	componentDidMount() {
		this.isCalendarRemindMode = !shouldShowShowtimes();
		const calendarNeeded = this.calendarNeeded();
		tabbingDetection();
		hoverDetection();
		window.addEventListener('orientationchange', this.handleOrientationChange);
		window.addEventListener('resize', this.handleResize);
		window.addEventListener('scroll', this.scrolling, true);
		window.addEventListener('popstate', this.handlePopState);
		document.addEventListener('visibilitychange', this.handleVisibilityChange);

		this.handleOrientationChange();

		document.body.classList.add('js-loaded');

		if (isIframed()) {
			document.body.classList.add('is-iframed');
		}

		const occluder = this.base.querySelector(`.${s.loadingOccluder}`);
		if (occluder) {
			const occluderStyle = getComputedStyle(occluder);
			const occluderHidden = occluderStyle.display === 'none' || occluderStyle.visibility === 'hidden' || occluderStyle.opacity === '0';
			if (occluderHidden) {
				this.setState({ contentVisible: true });
			}
		}

		Store.get().client.set({ headerHeight: getHeaderHeight() });

		let query = parseQueryString();
		Store.emit('LANDING_QUERY', query);

		if (this.cookiesEnabled) {
			Store.emit('CHECK_COOKIES', pixelsCategories);
			if (this.powsterCookiesEnabled) {
				Promise.all([
					import(/* webpackChunkName: "cookies" */ 'components/bolt/cookies/cookies'),
					import(/* webpackChunkName: "cookiesPreference" */ 'components/bolt/cookiesPreference/cookiesPreference')
				]).then(([cookiesComponent, cookiesPreferenceComponent]) => {
					this.Cookies = cookiesComponent.default;
					this.CookiesPreference = cookiesPreferenceComponent.default;
					this.forceUpdate();
				});
			}
		} else {
			Store.emit('ACCEPT_ALL_COOKIES', { doNotSetCookie: true });
		}

		if (this.useKetchCookies) {
			import('utils/gdpr/ketch');
		}
		if (this.trustArcCookiesEnabled) {
			import('utils/gdpr/trustArc');
		}

		if (this.ageGateEnabled) {
			const cookieName = this.state.appState?.appData?.options?.ageGate?.cookieName || 'pow-agegate';
			const lockOut = this.state.appState?.appData?.options?.ageGate?.lockOut;
			const ofAge = query.ag === 'adult' ? true : getCookie(cookieName);
			if (ofAge !== null) {
				Store.emit('AGEGATE_CONSENT', +ofAge);
				// this.ageGateEnabled = false;
				if (!lockOut || ofAge > 0) {
					Store.emit('AGEGATE_CLOSE');
				}
			}
			import(/* webpackChunkName: "ageGate" */ 'components/bolt/ageGate/ageGate').then(component => {
				this.AgeGate = component.default;
				this.forceUpdate();
			});
		}

		if (this.externalLinkModalEnabled) {
			import(/* webpackChunkName: "externalLinkModal" */ 'components/bolt/externalLinkModal/externalLinkModal').then(component => {
				this.ExternalLinkModal = component.default;
				this.forceUpdate();
			});
		}

		if (this.userFeedbackModalEnabled) {
			import(/* webpackChunkName: "userFeedbackModal" */ 'components/bolt/userFeedbackModal/userFeedbackModal').then(component => {
				this.UserFeedbackModal = component.default;
			});
		}

		if (this.state.appState?.appData?.options?.videoOverlay) {
			this.loadTrailerOverlay();
		}

		if (this.state.appState?.appData?.options?.geoPopup) {
			import(/* webpackChunkName: "geoPopup" */ 'components/bolt/geoPopup/geoPopup').then(component => {
				this.GeoPopup = component.default;
			});
		}

		if (Object.values(this.state.appState?.appData?.pages).find(page => page.type === 'pressKit')) {
			import(/* webpackChunkName: "pressKitGalleryOverlay" */ 'components/bolt/pressKit/pressKitGalleryOverlay').then(component => {
				this.PressKitGalleryOverlay = component.default;
			});
		}

		if (this.state.appState?.appData?.options?.landingAudio) {
			import(/* webpackChunkName: "landingAudio" */ 'components/bolt/landingAudio/landingAudio').then(component => {
				this.LandingAudio = component.default;
				this.forceUpdate();
			});
		}

		const signUpBanner = this.state.appState?.appData?.options?.formEmbeds?.signUp;
		if (Object.values(signUpBanner || {}).filter(e => e).length) {
			import(/* webpackChunkName: "signUpForm" */ 'components/bolt/signUpForm/signUpForm').then(component => {
				this.SignUpForm = component.default;
			});
		}

		if ((this.isCalendarRemindMode || calendarNeeded) && !this.state.calRemindLoaded && !this.calRemindLoadStarted) {
			this.calRemindLoadStarted = true;
			import(/* webpackChunkName: "calendarRemind" */ 'components/bolt/calendarRemind/calendarRemind').then(component => {
				this.CalRemind = component.default;
			});
		}

		this.updateBgStyle();

		this.updateVisibilityPercentages();
		// Refresh things (active section, percentages) when things are loaded
		Store.on('SECTION_LOADED', this.scrolling);

		// If PLAY_BACKGROUND_VIDEO is emitted (from user first interacting with page), try to play the video
		Store.on('PLAY_BACKGROUND_VIDEO', this.playVideo);

		const visibleScrollbar = accessNested(this.state.appState, 'appData.options.paramountFeatures.visibleScrollbar', {});
		const colorVariables = accessNested(this.state.appState, 'appData.styling.variables', {});
		if (visibleScrollbar?.color) {
			let color = visibleScrollbar.color.replace('$', '');
			addCSSRuleset('#mainScroll::-webkit-scrollbar-thumb', {
				background: colorVariables[color] || color
			});
		}
		if (visibleScrollbar?.backgroundColor) {
			let color = visibleScrollbar.backgroundColor.replace('$', '');
			addCSSRuleset('#mainScroll::-webkit-scrollbar-track', {
				background: colorVariables[visibleScrollbar.color] || color
			});
		}

		const mailchimpData = accessNested(this.state.appState, 'appData.options.formEmbeds.popup', {});
		if (!mailchimpData.id && mailchimpData.baseUrl && mailchimpData.uuid && mailchimpData.lid && mailchimpData.landingDelay) {
			Store.emit('SUBSCRIBER_POPUP', mailchimpData, true);
		}

		if (query.sync) {
			import(/* webpackChunkName: "sync" */ 'utils/sync').then(module => module.prepareSync());
		}
	}

	componentWillUnmount() {
		window.removeEventListener('orientationchange', this.handleOrientationChange);
		window.removeEventListener('scroll', this.scrolling, true);
		window.removeEventListener('popstate', this.handlePopState);
		document.removeEventListener('visibilitychange', this.handleVisibilityChange);
		Store.off('SECTION_LOADED', this.scrolling);
		Store.off('PLAY_BACKGROUND_VIDEO', this.playVideo);
		clearAllBodyScrollLocks();
		window.clearTimeout(this.videoTimeout);
	}

	componentDidUpdate(prevProps, prevState) {
		const appState = this.state.appState;

		// This is being called constantly
		// this.handleOrientationChange();
		if (prevState.appState.activePage !== appState.activePage) {
			this.updateVisibilityPercentages();
			this.getBgData();
			this.updateBgStyle();
		}

		if (
			prevState.appState.homeActivePercentage > 0 && !appState.homeActivePercentage ||
			!prevState.appState.homeActivePercentage && appState.homeActivePercentage > 0
		) {
			this.getBgData();
			this.updateBgStyle();
		}

		const showTrailerOverlay = appState.trailerOverlay?.overlayOpen && appState.trailerOverlay?.data?.video;
		if (showTrailerOverlay) {
			this.loadTrailerOverlay();
		}

		if (!this.cookieHeight && !isServer) {
			const cookieContainer = document.querySelector('[data-section=cookies]');
			this.cookieHeight = cookieContainer && window.getComputedStyle(cookieContainer).height;
		}

		const isDesktopSize = accessNested(this.state, 'appState.client.isDesktopSize');
		const prevIsDesktopSize = accessNested(prevState, 'appState.client.isDesktopSize');
		const isDesktopWidth = accessNested(this.state, 'appState.client.isDesktopWidth');
		const prevIsDesktopWidth = accessNested(prevState, 'appState.client.isDesktopWidth');

		if (isDesktopSize !== prevIsDesktopSize || isDesktopWidth !== prevIsDesktopWidth) {
			Store.get().client.set({ headerHeight: getHeaderHeight() });
		}

		const { homeActivePercentage } = this.state.appState;
		const homePercentagePausePoint = isDesktopSize ? 0.6 : 0.4;

		if (this.$bgVideo) {
			const loopDisabled = this.getBgValue('disableLoop');
			const videoEnded = this.$bgVideo.currentTime >= this.$bgVideo.duration;
			if (homeActivePercentage >= homePercentagePausePoint && this.$bgVideo.paused && !(loopDisabled && videoEnded)) {
				this.$bgVideo.play();
			} else if (homeActivePercentage < homePercentagePausePoint) {
				this.$bgVideo.pause();
			}
		}

		if (this.state.appState.debugMode && !this.loadingDebugInfo) {
			this.loadingDebugInfo = true;
			import(/* webpackChunkName: "debugInfo" */ 'components/bolt/debugInfo/debugInfo').then(component => {
				this.DebugInfo = component.default;
				this.forceUpdate();
			});
		}

		let bgSources = [...this.$bgVideo?.children || []].map(s => s.src).join(',');
		if (this.previousBgSources && bgSources !== this.previousBgSources) {
			this.$bgVideo?.load();
		}
		this.previousBgSources = bgSources;
	}

	runOnLoad() {
		const { appData } = this.state.appState;
		const trackingData = appData?.tracking;
		tracking.init(trackingData.toJS ? trackingData.toJS() : trackingData);
		this.handleResize();
		// NOTE: should we delay this until componentDidMount? or setTimeout(, 0)?
		Store.emit('LANDING', { page: getPageIdFromURL(appData?.pages) || window.__START_PAGE_ID__ });
	}

	loadTrailerOverlay() {
		if (this.trailerOverlayLoaded) return;
		this.trailerOverlayLoaded = true;
		import(/* webpackChunkName: "trailerOverlay" */ 'components/bolt/trailerOverlay/trailerOverlay').then(component => {
			this.TrailerOverlay = component.default;
			this.forceUpdate();
		});
	}

	get sidePanelOpen() {
		const appState = this.state.appState;
		return appState.showtimes?.show || appState.homeEnt?.show;
	}

	getBgData() {
		const pagesData = this.state.appState?.appData?.pages || {};
		const activePage = this.state.appState.activePage || '';
		const activePageType = pagesData[activePage]?.type;
		const activePageBgData = activePageType !== 'showtimes' ? pagesData[activePage]?.data?.bg : null;

		const { homeActivePercentage } = this.state.appState;
		const homeBgData = pagesData.boltHome?.data?.bg || {};

		if (homeActivePercentage > 0) {
			this.backgroundData = homeBgData;
		} else {
			this.backgroundData = activePageBgData;
		}
	}

	handleVisibilityChange() {
		if (typeof document === 'undefined' || document.hidden || !this.$bgVideo) {
			return;
		}
		let stopped = this.$bgVideo.paused || this.$bgVideo.currentTime === 0 || this.$bgVideo.ended;
		if (stopped && this.$bgVideo.readyState >= 2) {
			// For some reason if you play it too soon on IOS, it freezes even though the video meets all the conditions above
			this.videoTimeout = window.setTimeout(() => {
				// Make sure the video still exists after the timeout
				if (this.$bgVideo) {
					this.$bgVideo.play();
				}
			}, 100);
		}
	}

	updateBgStyle() {
		if (this.backgroundData) {
			if (this.backgroundData.useSolidBgColor) {
				return;
			}

			this.setState({
				backgroundStyle: {
					backgroundImage: this.getBgImage(),
					backgroundPosition: this.getBgValue('bgPosition')
				},
				fallbackBackgroundStyle: { objectPosition: this.getBgValue('bgPosition') }
			});
		} else {
			this.setState({
				backgroundStyle: {},
				fallbackBackgroundStyle: {}
			});

		}
	}

	removeBgVideo() {
		videosWithError[this.state.bgVideo] = true;
		this.setState({ bgVideo: false });
	}

	scrolling() {
		const headerOpen = accessNested(this.state, 'appState.header.open');
		const ignoreScroll = accessNested(this.state, 'appState.ignoreScroll');

		if (headerOpen || ignoreScroll) {
			return;
		}

		this.updateVisibilityPercentages();
	}

	updateVisibilityPercentages() {
		Store.emit('UPDATE_VISIBILITY_PERCENTAGES');
	}

	calendarNeeded() {
		const appData = this.state.appState.appData;
		if (appData?.options?.useCalendar) return true;
		const noShowtimesButtonPath = accessNested(appData, 'pages.boltShowtimes.data.noShowtimes.buttons.extraButton.path', null);
		const homeButton1Path = accessNested(appData, 'pages.boltHome.data.button1.path', null);
		const homeButton2Path = accessNested(appData, 'pages.boltHome.data.button2.path', null);
		const persistentPath = accessNested(appData, 'header.powsterHeader.persistentButton.path', null);
		return [noShowtimesButtonPath, homeButton1Path, homeButton2Path, persistentPath].includes('@calendarreminder');
	}

	getHeaderOffset() {
		const { appState } = this.state;
		const { appData, cookies, externalCookieBanner } = appState;

		let headerOffset = 0;

		const sonyHeaderEnabled = appData?.options?.sonyFeatures?.sonyHeader && !getIsSinglePageIframed();
		if (sonyHeaderEnabled) {
			headerOffset = 34;
		}

		const externalCookieBannerOpen = externalCookieBanner?.bannerOpen;
		const cookieMode = externalCookieBannerOpen || (cookies?.pushDown && cookies?.cookieBannerOpen);
		if (cookieMode) {
			const cookieHeight = (externalCookieBannerOpen ? externalCookieBanner.height : cookies?.height) || 0;
			headerOffset += cookieHeight;
		}

		return headerOffset;
	}

	getBgValue(field) {
		if (!this.backgroundData || !this.backgroundData.desktop && !this.backgroundData.tablet && !this.backgroundData.mobile) {
			return undefined;
		}

		let bgValue = {};

		Object.entries(this.backgroundData).map(([device, data]) => {
			bgValue[device] = data?.[field];
		});

		return useDynamicAssetFolder(getValueForClientSize(bgValue));
	}

	getFallbackFormat() {
		const store = Store.get();

		// TODO: more flexibility, why are we hardocding boltHome?
		let page = store.activePage === 'boltSplashHub' ? 'boltSplashHub' : 'boltHome';
		return accessNested(store, `appData.pages.${page}.data.bg.alternativeImageFormat`, 'jpg');
	}

	getBgImage() {
		let asset = this.getBgValue('asset');
		const fallbackFormat = this.getFallbackFormat();
		let videoExtensions = ['mp4', 'webm', 'mov'];
		let staticBackground = false;

		// Check for boltHome static background
		staticBackground = this.state.appState.appData?.pages?.boltHome?.data?.bg?.staticBackground;

		// If asset is a video, bg image will be the fallback image instead
		if (videoExtensions.includes(asset?.split?.('.').pop())) {
			const bgVideo = staticBackground || videosWithError[asset] ? false : asset;
			if (this.state.bgVideo !== bgVideo) {
				this.setState({ bgVideo });
			}
			const bgValueKey = staticBackground ? 'staticBackgroundImg' : 'fallbackImg';
			asset = this.getBgValue(bgValueKey);
		} else {
			if (this.state.bgVideo) {
				this.setState({ bgVideo: false });
			}
		}

		// Makes sure the background image is fully loaded before showing it
		if (!this.state.bgImageLoaded) {
			if (asset) {
				if (!this.occluderTimeout) {
					const image = new Image();
					image.onload = () => this.setState({ bgImageLoaded: true });
					image.onerror = image.onload;
					image.src = webp.getAssetUrl(asset, fallbackFormat);
					// If the image takes too long to load, better to show the content
					this.occluderTimeout = setTimeout(() => {
						if (!this.state.bgImageLoaded) {
							this.setState({ bgImageLoaded: true });
						}
					}, 5000);
				}
			} else {
				this.setState({ bgImageLoaded: true });
			}
		}

		return webp.css(asset, fallbackFormat);
	}

	getH1(appData, page, activePageDetails, activePage) {
		let fullPageName = [
			appData?.meta?.title?.locale,
			appData?.copy?.baseCopy[page?.name] || appData?.copy?.baseCopy[activePageDetails?.name] || page?.name || activePageDetails?.name || activePage?.replace(/^bolt/, '')
		].filter(e => e).join(' | ');

		if (activePageDetails?.h1) {
			fullPageName = activePageDetails.h1;
		}

		return <h1 class={s.pageName}>{fullPageName}</h1>;
	}

	closeAllOverlays() {
		Store.emit('CLOSE_ALL_OVERLAYS');
	}

	handleOrientationChange() {
		this.handleResize();
		let orientation = 'portrait';
		switch (window.orientation) {
			case 0:
			case 180:
				// Portrait
				orientation = 'portrait';
				break;
			case -90:
			case 90:
				orientation = 'landscape';
				break;
		}
		this.setState({ orientation });
	}

	handleResize() {
		Store.emit('UPDATE_CLIENT');
		let vh = window.innerHeight;
		document.documentElement.style.setProperty('--vh', (vh * 0.01) + 'px');
		document.documentElement.style.setProperty('--windowHeight', vh + 'px');
		let isDesktopSize = getIsDesktopSize();
		let client = this.state.appState.client;
		if (client && isDesktopSize !== client.isDesktopSize) {
			Store.get().client.set({ isDesktopSize });
		}
		this.updateBgStyle();
	}

	hidePoster() {
		this.setState({ posterVisible: false });
	}

	handlePopState(e) {
		let newPage = e.state?.activePage;
		// TODO: also check if newPage is not a valid page (e.g. state stored for a previous version was a page that doesn't exist anymore)
		if (!newPage) {
			// Page id not in popped state, try to detect from URL
			newPage = getPageIdFromURL();
		}
		Store.emit('POP_STATE', newPage || window.__START_PAGE_ID__);
	}

	onPageContentLoad() {
		if (this.state.isComponentLoaded !== true) {
			this.setState({ isComponentLoaded: true });
		}
	}

	getExtra() { }

	playVideo() {
		if (!this.state.bgVideo || this.state.videoPlaySuccess) return;
		if (!this.state.bgVideoHidden) {
			// try to autoplay the bg video on page load
			tryToPlayVideo(this.$bgVideo, true).then(
				() => {
					this.hidePoster();
					this.setState({ videoPlaySuccess: true });
				}
			).catch(
				// if it fails, hide the video (and use the fallback image instead)
				() => {
					this.setState({ bgVideoHidden: true });
				}
			);
		} else {
			// when user first touches screen, try to play the video again
			tryToPlayVideo(this.$bgVideo).then(
				() => {
					this.hidePoster();
					this.setState({ bgVideoHidden: false, videoPlaySuccess: true });
				}
			// if it still fails, remove it completely
			).catch(this.removeBgVideo);
		}
	}

	toggleAudio() {
		if (!this.$bgVideo) {
			return;
		}

		this.setState({ audioMuted: !this.$bgVideo.muted });
		this.$bgVideo.muted = !this.$bgVideo.muted;
	}

	checkIfLoaded() {
		return this.state.isComponentLoaded && (this.state.bgImageLoaded || !this.backgroundData || this.backgroundData.useSolidBgColor);
	}

	occluderRemoved() {
		this.setState({ contentVisible: true });
	}

	renderLoadingOccluder() {
		return <div class={s.loadingOccluder} onAnimationEnd={this.occluderRemoved} key="occluder" />;
	}

	renderBgVideo() {
		const { appState, bgVideo, backgroundStyle, fallbackBackgroundStyle, posterVisible } = this.state;

		const { activePage } = appState;
		const isPageDedicatedPage = activePage !== 'boltHome' && activePage !== 'boltSplashHub' && !appState.appData.pages[activePage]?.section;
		const showtimesShowing = accessNested(appState, 'showtimes.show', false);
		const homeActivePercentage = appState.homeActivePercentage ?? 1;
		const hideBackground = !homeActivePercentage || showtimesShowing || isPageDedicatedPage;

		const preLoadImg = this.getBgValue('preLoadImg');
		const fallbackFormat = this.getFallbackFormat();
		const accessibilityCopy = accessNested(appState, 'appData.copy.accessibilityCopy', {});

		return (
			<div class={joinClasses({ s, bgVideo: true, hideBackground })} key="bgVideo">
				<video
					style={backgroundStyle}
					class={this.state.bgVideoHidden && s.hidden}
					muted={this.state.audioMuted}
					autoPlay
					playsInline
					loop={!this.getBgValue('disableLoop')}
					poster={isWebPSupported ? preLoadImg : preLoadImg.replace(/\.[^.]*$/, fallbackFormat)}
					onCanPlay={this.playVideo}
					onError={this.removeBgVideo}
					ref={$ref => this.$bgVideo = $ref}
					ariaLabel={accessibilityCopy.$BACKGROUND_VIDEO_ARIA}
				>
					{bgVideo.endsWith('.webm') && <source src={bgVideo} type="video/webm" />}
					<source src={bgVideo.replace(/\.[^./]+$/, '.mp4')} type="video/mp4" />
				</video>
				{posterVisible && <webp.Picture src={preLoadImg} alt="" fallbackFormat={fallbackFormat} style={fallbackBackgroundStyle} />}
			</div>
		);
	}

	renderBackground() {
		if (this.state.bgVideo) {
			return this.renderBgVideo();
		}

		const { appState } = this.state;
		const { appData, client, cookies, externalCookieBanner } = appState;
		const isMobile = !client?.isDesktopSize;

		const externalCookieBannerOpen = externalCookieBanner?.bannerOpen;
		const cookieMode = externalCookieBannerOpen || (cookies?.pushDown && cookies?.cookieBannerOpen);

		const cookieStyle = {};
		if (cookieMode && typeof window !== 'undefined') {
			const top = this.getHeaderOffset();
			cookieStyle.top = `${top}px`;
		}

		const homeActivePercentage = appState.homeActivePercentage ?? 1;
		// Hides the background to prevent it being visible on iOS overscroll
		const hideBackground = !homeActivePercentage && isMobile;

		return (
			<div
				class={joinClasses({
					s, background: true,
					sidePanelOpen: this.sidePanelOpen,
					hasStickyFooter: appData?.footer?.sticky,
					cookieMode,
					hideBackground,
					sidePanelAnimation: appState.showtimes?.animate || appState.homeEnt?.animate,
					hasMiniGallery: appData?.options?.paramountFeatures?.toutCarousel?.contentCarousel
				})}
				style={{ ...this.state.backgroundStyle, ...cookieStyle }}
				key="background"
				role="img"
				ariaLabel={appData?.copy?.accessibilityCopy?.$BACKGROUND_IMAGE_ARIA}
			/>
		);
	}

	renderMain(props, state) {
		const { appState, audioMuted, bgVideo } = state;

		const {
			activePage,
			appData,
			client,
			videos,
			header,
			scrollerTallerThanWindow,
			widgetFullyExpanded,
			externalCookieBanner,
		} = appState;

		let mainPage = activePage;
		let activePageDetails = appData?.pages?.[activePage];
		if (activePageDetails?.section) {
			// TODO: ability to attach sections to different pages
			mainPage = Object.values(pages).find(page => page.page === 'home')?.id || 'boltHome';
		}
		// For overlays (e.g. boltShowtimes), activePage changes but it is not in pages
		//   when that's the case, keep using the previous one
		let page = pages[mainPage] || this.currentPage;
		if (activePageDetails?.customPageComponent && pages[activePageDetails.customPageComponent]) {
			page = { ...pages[activePageDetails.customPageComponent], id: activePageDetails.id };
		}
		if (props.renderedPage) {
			page = props.renderedPage;
		}
		this.currentPage = page;

		const orientation = state.orientation;
		const isMobile = !client?.isDesktopSize;
		const isMobileLandscape = (client?.deviceSize === 'mobile') && (orientation === 'landscape');
		const isTabletLandscape = (client?.device !== 'desktop') && !isMobileLandscape && (orientation === 'landscape');
		const videoOverlayOpen = videos?.overlayOpen;
		const sidePanelOpen = this.sidePanelOpen;
		// const headerOpen = accessNested(this.state, 'appState.header.open', false);
		const homeActivePercentage = accessNested(appState, 'homeActivePercentage', 1);
		const hubActivePercentage = accessNested(appState, 'hubActivePercentage', 1);
		const externalCookieBannerOpen = externalCookieBanner?.bannerOpen;
		// Makes the whole main page one big scrolling section to cater for the giant Evidon banner (or for other reasons)
		const oneContinuousScroll = (externalCookieBannerOpen && isMobile) || appData?.options?.oneContinuousScroll;

		const sonyFooterEnabled = accessNested(appData, 'footer.sonyFooter', false);
		const disableMobileLandscape = accessNested(appData, 'options.disableLandscape');
		const disableTabletLandscape = accessNested(appData, 'options.disableTabletLandscape');
		const landingAudio = accessNested(appData, 'options.landingAudio', null);
		const visibleScrollbar = accessNested(appData, 'options.paramountFeatures.visibleScrollbar', false);
		const iframeLandscapeEnabled = accessNested(appData, 'pages.boltIframe.data.disableOrientationLock', false) && activePage === 'boltIframe';
		const showLandscapeOverlay = ((isMobileLandscape && disableMobileLandscape) || (isTabletLandscape && disableTabletLandscape)) && !iframeLandscapeEnabled;
		const videoHasAudio = accessNested(appData, 'pages.boltHome.data.bg.videoHasAudio', false);
		const iconPosition = accessNested(appData, 'pages.boltHome.data.bg.iconPosition', 'top-left');

		const mainScrollStyle = {};
		if (visibleScrollbar) {
			mainScrollStyle.scrollbarColor = `${visibleScrollbar.color} ${visibleScrollbar.backgroundColor}`;
		}

		let headerOffset = this.getHeaderOffset();

		const headerHeight = accessNested(Store.get(), 'client.headerHeight', 0);
		const desktopSolidHeader = accessNested(appData, 'header.powsterHeader.desktopSolidHeader', null);
		const hub = accessNested(appData, 'pages.boltHubPage') && !accessNested(appData, 'pages.boltHome');
		const previewPanelReplacingHomePage = appData?.pages.boltPreviewPanel && !appData?.pages.boltHome;
		const dedicatedPage = pages[activePage] && activePage !== 'boltHome' && !activePageDetails?.section;
		const homeInactive = (homeActivePercentage <= getPercentageThreshold(headerOffset)) || dedicatedPage;
		const hubInactive = (hubActivePercentage <= getPercentageThreshold(headerHeight));
		const pageInactive = hub ? hubInactive : homeInactive;
		// TODO: detect if ANY overlay is open in a generic way
		// TODO: ability to control header background via appData
		let headerSolidBackground = pageInactive && !sidePanelOpen && !videoOverlayOpen;
		if (previewPanelReplacingHomePage || !scrollerTallerThanWindow || widgetFullyExpanded) {
			headerSolidBackground = false;
		}
		// Potentially wait for other initial things to load
		const loaded = this.checkIfLoaded();

		let hasShowtimes = Object.values(appData.pages).some(p => p?.type === 'showtimes');
		let hasHomeEnt = Object.values(appData.pages).some(p => p?.type === 'homeEnt');
		let hasPressKit = Object.values(appData.pages).some(p => p?.type === 'pressKit');

		let bgRender = this.renderBackground();

		let pageProps = {};
		if (page) {
			pageProps = {
				store: appState,
				client: appState.client,
				appData,
				id: page.id,
				data: appData.pages[page.id]?.data,
				bgRender: oneContinuousScroll && bgRender
			};
			if (page.page) {
				pageProps[page.page] = appState[page.page];
			}
		}

		return (
			<div
				class={joinClasses(
					s.mainScroll,
					loaded && 'contentLoaded',
					state.contentVisible && 'contentVisible',
					(!visibleScrollbar || isMobile) && s.hideScrollbar,
					dedicatedPage && s.dedicatedPage
				)}
				id="mainScroll"
				style={mainScrollStyle}
				key="mainScroll"
			>
				{!this.hideHeader && (
					<Header
						closeAllOverlays={this.closeAllOverlays}
						client={client}
						appData={appData}
						activePage={activePage}
						solidBackground={headerSolidBackground}
						headerData={header}
						offsetTop={headerOffset}
						isCalendarRemindMode={this.isCalendarRemindMode}
						calRemindOpen={appState.calRemindOpen}
						desktopSolidHeader={desktopSolidHeader}
						footerData={appState.footer}
						key="header"
					/>
				)}
				{this.getH1(appData, page, activePageDetails, activePage)}
				{bgRender}
				{this.CalRemind && <this.CalRemind appData={appData} open={appState.calRemindOpen} offsetTop={headerOffset} key="calendarRemind" />}
				<DynamicPageLoader
					class={s.pageContainer}
					containerClass={joinClasses(s.pageWrapper, 'pageWrapper')}
					page={page}
					props={pageProps}
					onLoad={this.onPageContentLoad}
					key="dynamicPageLoader"
				/>
				{(!sidePanelOpen || sonyFooterEnabled) && <Footer data={appData.footer} store={appState} key="footer" />}
				{VideoOverlayLoader && <VideoOverlayLoader store={appState} key="videoLoader" />}
				{hasShowtimes && <SidePanelLoader store={appState} key="showtimesLoader" />}
				{hasHomeEnt && <SidePanelLoader store={appState} key="homeEntLoader" />}
				{hasPressKit && <SidePanelLoader store={appState} key="pressKitLoader" />}
				{this.TrailerOverlay && <this.TrailerOverlay store={appState} />}
				{this.GeoPopup && <this.GeoPopup store={appState} />}
				{this.PressKitGalleryOverlay && <this.PressKitGalleryOverlay store={appState} />}
				{this.LandingAudio && <this.LandingAudio data={landingAudio} offsetTop={headerOffset} store={appState} />}
				{(videoHasAudio && bgVideo) && (
					<div onClick={this.toggleAudio} class={joinClasses(s.sound, audioMuted && s.muted, s[iconPosition])}>
						<button class={s.icon} />
					</div>
				)}
				{(!videoOverlayOpen && showLandscapeOverlay) && <LandscapeOverlay key="landscape-overlay" />}
				{this.renderLoadingOccluder()}
				{this.getExtra()}
			</div>
		);
	}

	render(props, state) {
		const {
			appData,
			client,
			cookies,
			externalCookieBanner,
			debugMode,
			footer,
			header
		} = state.appState;

		const cookiePrefsOpen = cookies.cookiePreferencesOpen;
		const cookieBannerOpen = cookies.cookieBannerOpen;
		let ageGateOpen = state.appState.ageGateOpen !== false;
		const ageGateOnlyOnPages = appData?.options?.ageGate?.onlyOnPages;
		if (ageGateOpen && ageGateOnlyOnPages) {
			const activePageIsSection = appData.pages[state.appState.activePage].section;
			ageGateOpen = ageGateOnlyOnPages.split(',').map(e => e.trim()).includes(state.appState.activePage) && !activePageIsSection;
		}

		const headerType = Object.keys(appData.header).find(name => name.endsWith('Header') && name !== 'powsterHeader');
		const footerType = ['sonyFooter'].find(e => appData.footer[e]) || 'defaultFooter';
		const navOpen = header.open;

		let DebugInfo = debugMode && this.DebugInfo;
		let { Cookies, CookiesPreference, AgeGate, ExternalLinkModal, UserFeedbackModal, SignUpForm } = this;
		const isRTL = appData?.styling?.fontVariables?.textDirection === 'rtl';

		const style = `--footer-covered-screen-size: ${footer?.coveredScreenSize || 0}px;`;

		const children = [
			Cookies && <Cookies appData={appData} categories={pixelsCategories} hidden={!cookieBannerOpen} key="cookieBanner" />,
			<div class={s.mainScrollWrapper} key="mainScrollWrapper">{this.renderMain(props, state)}</div>,
			CookiesPreference && <CookiesPreference appData={appData} copy={appData.copy.baseCopy} categories={pixelsCategories} key="cookiesPref" cookiePrefsOpen={cookiePrefsOpen} />,
			AgeGate && ageGateOpen && <AgeGate appData={appData} copy={appData.copy} key="ageGate" />,
			ExternalLinkModal && <ExternalLinkModal appData={appData} externalLinkModal={state.appState.externalLinkModal} copy={appData.copy} key="externalLink" />,
			UserFeedbackModal && <UserFeedbackModal appData={appData} data={state.appState.userFeedbackModal} copy={appData.copy} key="userFeedback" />,
			SignUpForm && <SignUpForm data={appData?.options?.formEmbeds?.signUp} activePage={state.appState.activePage} clientData={client} cookies={cookies} externalCookieBanner={externalCookieBanner} showtimesProviderInterstitialOpen={state.appState.showtimes?.providerInterstitialOpen} navOpen={navOpen} key="signUp" />,
			DebugInfo && <DebugInfo appData={appData} appState={state.appState} key="debug" />,
		].filter(e => e);

		return (
			<div
				class={joinClasses(
					s.container,
					client.device,
					client.orientation,
					client.isIOS && 'iOS',
					client?.isSafari && 'isSafari',
					headerType,
					footerType,
					'page-' + state.appState.activePage,
					isRTL && 'isRTL'

				)}
				id="bolt"
				style={style}
			>
				{children}
			</div>
		);
	}
}
